import { useEffect, useState } from "react";
import { Cookie, Settings2, X } from "lucide-react";
import {
  acceptAll,
  getConsent,
  onOpenPreferences,
  rejectAll,
  setConsent,
  type ConsentState,
} from "@/lib/consent";
import { cn } from "@/lib/utils";

const CookieConsent = () => {
  const [state, setState] = useState<ConsentState | null>(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const current = getConsent();
    setState(current);
    setAnalytics(current.categories.analytics);
    setMarketing(current.categories.marketing);
    const off = onOpenPreferences(() => {
      const c = getConsent();
      setAnalytics(c.categories.analytics);
      setMarketing(c.categories.marketing);
      setShowPrefs(true);
    });
    return off;
  }, []);

  if (!state) return null;

  const bannerVisible = !state.decided && !showPrefs;

  const handleAcceptAll = () => {
    acceptAll();
    setState({ ...state, decided: true, categories: { necessary: true, analytics: true, marketing: true } });
    setShowPrefs(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setState({ ...state, decided: true, categories: { necessary: true, analytics: false, marketing: false } });
    setShowPrefs(false);
  };
  const handleSave = () => {
    setConsent({ analytics, marketing });
    setState({ ...state, decided: true, categories: { necessary: true, analytics, marketing } });
    setShowPrefs(false);
  };

  return (
    <>
      {/* Banner inicial */}
      {bannerVisible && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-5"
        >
          <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 backdrop-blur-md shadow-2xl shadow-primary/10 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center">
                <Cookie size={16} />
              </div>
              <div className="min-w-0">
                <h2 id="cookie-banner-title" className="sr-only">Preferencias de cookies</h2>
                <p className="text-sm text-muted-foreground">
                  Usamos cookies necesarias y, si aceptas, de analítica y marketing para medir campañas.{" "}
                  <a href="/cookies" className="underline hover:text-foreground">Política de cookies</a>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border border-input bg-background hover:bg-accent transition-colors"
              >
                <Settings2 size={16} /> Configurar
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-medium border border-input bg-background hover:bg-accent transition-colors"
              >
                Rechazar todo
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Aceptar todo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de preferencias */}
      {showPrefs && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-prefs-title"
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-foreground/40 backdrop-blur-sm"
        >
          <div className="relative w-full max-w-xl rounded-2xl border border-border bg-background shadow-2xl">
            <button
              type="button"
              onClick={() => setShowPrefs(false)}
              aria-label="Cerrar"
              className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-accent transition-colors"
            >
              <X size={18} />
            </button>
            <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto">
              <h2 id="cookie-prefs-title" className="font-display text-xl uppercase mb-1">
                Preferencias de cookies
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Decide qué cookies quieres activar. Tu elección se guarda durante 12 meses y puedes cambiarla cuando
                quieras desde el pie de página.
              </p>

              <CategoryRow
                title="Estrictamente necesarias"
                description="Imprescindibles para que la web funcione (sesión, seguridad, recordar tu elección de cookies). No se pueden desactivar."
                checked
                locked
              />
              <CategoryRow
                title="Analítica"
                description="Nos permiten medir cómo se usa la web (páginas vistas, origen del tráfico) para mejorarla. No se usan para perfilarte."
                checked={analytics}
                onChange={setAnalytics}
              />
              <CategoryRow
                title="Marketing"
                description="Píxel de Meta (Facebook/Instagram) y similares. Nos ayudan a medir la eficacia de nuestras campañas y mostrarte anuncios más relevantes."
                checked={marketing}
                onChange={setMarketing}
              />

              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end mt-6">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-medium border border-input bg-background hover:bg-accent transition-colors"
                >
                  Rechazar todo
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-medium border border-input bg-background hover:bg-accent transition-colors"
                >
                  Aceptar todo
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Guardar preferencias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

type RowProps = {
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
};
const CategoryRow = ({ title, description, checked, locked, onChange }: RowProps) => (
  <div className="flex items-start justify-between gap-4 py-4 border-t border-border first:border-t-0">
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title}
      disabled={locked}
      onClick={() => onChange?.(!checked)}
      className={cn(
        "relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors",
        checked ? "bg-primary" : "bg-muted",
        locked && "opacity-60 cursor-not-allowed"
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-0.5"
        )}
      />
    </button>
  </div>
);

export default CookieConsent;
